import React, { useEffect, useState, useRef } from 'react';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';
import type { OnboardingStepProps } from '../onboardingSteps';
import { fetchSiteBranding, updateSiteBranding } from '../onboardingApi';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';

const BrandingOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange }) => {
  const [siteName, setSiteName] = useState('');
  const [tagline, setTagline] = useState('');
  const [loaderImage, setLoaderImage] = useState('');
  const [logo, setLogo] = useState('');
  const [favicon, setFavicon] = useState('');
  const [activeAssetTab, setActiveAssetTab] = useState<'loader' | 'logo' | 'favicon'>('loader');
  const [brandingId, setBrandingId] = useState<string | null>(null);
  const [existingData, setExistingData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [fileError, setFileError] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchSiteBranding();
        if (data && data._id) {
          setBrandingId(data._id);
          setSiteName(data.siteName || '');
          setTagline(data.tagline || '');
          setLoaderImage(data.loaderImage || '');
          setLogo(data.logo || '');
          setFavicon(data.favicon || '');
          setExistingData(data);
        }
      } catch {
        // No existing branding
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    onValidationChange(siteName.trim().length > 0);
  }, [siteName, onValidationChange]);

  useEffect(() => {
    if (loading) return;
    onPreviewChange?.({
      branding: {
        ...(existingData || {}),
        siteName,
        tagline,
        loaderImage,
        logo,
        favicon,
      },
    });
  }, [loading, existingData, siteName, tagline, loaderImage, logo, favicon, onPreviewChange]);

  useEffect(() => {
    saveRef.current = async () => {
      if (!brandingId) return;
      const payload: Record<string, unknown> = {
        siteName: siteName.trim(),
        tagline: tagline.trim(),
        navItems: existingData?.navItems || [],
        loaderImage,
        favicon,
        logo,
      };
      await updateSiteBranding(brandingId, payload);
    };
  }, [saveRef, brandingId, siteName, tagline, loaderImage, logo, favicon, existingData]);

  const openFileUpload = () => {
    fileInputRef.current?.click();
  };

  const processImageFile = (file: File) => {
    setFileError('');
    if (!['image/png', 'image/svg+xml', 'image/x-svg+xml'].includes(file.type)) {
      setFileError('Only PNG and SVG files are supported.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFileError('File size must be less than 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Data = event.target?.result as string;
      if (activeAssetTab === 'loader') setLoaderImage(base64Data);
      else if (activeAssetTab === 'logo') setLogo(base64Data);
      else setFavicon(base64Data);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    processImageFile(file);
  };

  const handleRemoveImage = () => {
    setFileError('');
    if (activeAssetTab === 'loader') setLoaderImage('');
    else if (activeAssetTab === 'logo') setLogo('');
    else setFavicon('');
  };

  const getImageUrl = () => {
    if (activeAssetTab === 'loader') return loaderImage;
    if (activeAssetTab === 'logo') return logo;
    return favicon;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-9 bg-gray-100 rounded-lg" />
        <div className="h-9 bg-gray-100 rounded-lg" />
        <div className="h-28 w-28 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 onb-fade-in">
      {/* Site Name */}
      <div className="space-y-1.5">
        <Label>
          Site name <span className="text-red-500">*</span>
        </Label>
        <Input
          value={siteName}
          onChange={(e) => setSiteName(e.target.value)}
          placeholder="Artist Name or Band Name"
        />
        <p className="text-xs text-muted-foreground">
          This appears at the top of your website.
        </p>
      </div>

      {/* Branding Assets Tabs */}
      <div className="space-y-1.5">
        <Label>Branding Assets <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <Tabs value={activeAssetTab} onValueChange={(value: string) => setActiveAssetTab(value as 'loader' | 'logo' | 'favicon')}>
          <TabsList className="w-full">
            <TabsTrigger value="loader" className="flex-1">Loader</TabsTrigger>
            <TabsTrigger value="logo" className="flex-1">Logo</TabsTrigger>
            <TabsTrigger value="favicon" className="flex-1">Favicon</TabsTrigger>
          </TabsList>

          {(['loader', 'logo', 'favicon'] as const).map((tab) => (
            <TabsContent key={tab} value={tab} className="mt-4">
              {!getImageUrl() ? (
                <Card
                  className={`group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-5 sm:py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                  onClick={openFileUpload}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="grid space-y-3">
                    <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                      <Upload className="size-5" />
                      <div>
                        Drop image here or{" "}
                        <Button
                          type="button"
                          variant="link"
                          className="text-primary p-0 h-auto font-normal"
                          onClick={(e) => {
                            e.stopPropagation();
                            openFileUpload();
                          }}
                        >
                          browse
                        </Button>{" "}
                        to upload
                      </div>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".png,.svg"
                    onChange={handleFileChange}
                  />
                  <span className="text-xs text-muted-foreground mt-1 block">
                    Supported: PNG, SVG (max 2 MB)
                  </span>
                </Card>
              ) : (
                <div className="space-y-2">
                  <Card
                    className={`group flex max-h-[280px] sm:max-h-[400px] w-full flex-col items-center justify-center gap-4 py-5 sm:py-8 border-dashed text-sm cursor-pointer transition-colors ${isDragActive ? "border-primary bg-muted/50" : "hover:bg-muted/50"}`}
                    onClick={openFileUpload}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="relative max-h-64 max-w-64 rounded overflow-hidden">
                      <img
                        src={getImageUrl()}
                        alt={tab}
                        className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                        title="Click to edit image"
                      />
                    </div>
                    <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                      <Upload className="size-4" />
                      <span>Click to edit or re-upload</span>
                    </div>
                  </Card>
                  {fileError && (
                    <p className="text-sm text-red-500">{fileError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={openFileUpload}
                    >
                      <RefreshCw className="size-3.5" />
                      Change
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-danger"
                      onClick={handleRemoveImage}
                    >
                      <Trash2 className="size-3.5" />
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default BrandingOnboarding;